/*
  Flor de Oro Customizer JS — Luxury Gift Context Version
  - Safe: does NOT hijack Add to Cart submit
  - Opens/closes modal
  - Moves modal to body
  - Handles steps
  - Reads saved occasions from localStorage key: fdo_CUSTOMER_ID
  - Auto-fills modal from saved occasions
  - Uses central GiftContext state
  - Copies modal values into hidden product-form inputs only when Save & continue is clicked
*/

(function () {
  'use strict';

  var FDO = {
    contexts: {},
    triggerMap: {}
  };

  var FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getFocusableElements(container) {
    return qsa(FOCUSABLE_SELECTORS, container).filter(function (el) {
      return !el.closest('[hidden]') && el.offsetWidth > 0;
    });
  }

  function trapFocus(modal) {
    if (!modal) return;

    var focusables = getFocusableElements(modal);
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    function handler(event) {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === first || document.activeElement === modal) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    modal._fdoTrapHandler = handler;
    modal.addEventListener('keydown', handler);
    first.focus();
  }

  function releaseFocus(modal) {
    if (!modal || !modal._fdoTrapHandler) return;
    modal.removeEventListener('keydown', modal._fdoTrapHandler);
    delete modal._fdoTrapHandler;
  }

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function safeKey(value) {
    return String(value || '')
      .replace(/[^a-zA-Z0-9_-]/g, '-')
      .toLowerCase();
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function emitNativeEvents(field) {
    if (!field) return;

    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setFieldValue(field, value) {
    if (!field) return;

    var finalValue = value == null ? '' : String(value);

    if (field.type === 'checkbox') {
      field.checked = !!value && value !== 'No' && value !== 'false';
      emitNativeEvents(field);
      return;
    }

    if (field.type === 'radio') {
      field.checked = field.value === finalValue;
      emitNativeEvents(field);
      return;
    }

    if (field.tagName === 'SELECT') {
      var exists = false;

      qsa('option', field).forEach(function (option) {
        if (option.value === finalValue) exists = true;
      });

      if (!exists && finalValue) {
        var option = document.createElement('option');
        option.value = finalValue;
        option.textContent = finalValue;
        field.appendChild(option);
      }

      field.value = finalValue;
      emitNativeEvents(field);
      return;
    }

    field.value = finalValue;
    emitNativeEvents(field);
  }

  function getValue(field) {
    if (!field) return '';

    if (field.type === 'checkbox') {
      return field.checked ? field.value || 'Yes' : '';
    }

    if (field.type === 'radio') {
      return field.checked ? field.value : '';
    }

    return field.value || '';
  }

  function getTrigger(modalId) {
    return qs('[data-fdo-open="' + modalId + '"]');
  }

  function getProductForm(modalId) {
    var trigger = getTrigger(modalId);
    if (!trigger) return null;

    return (
      trigger.closest('form[data-type="add-to-cart-form"]') ||
      trigger.closest('form[action*="/cart/add"]') ||
      trigger.closest('form')
    );
  }

  function ensureHiddenContainer(formEl) {
    if (!formEl) return null;

    var container = qs('[data-fdo-hidden-props]', formEl);

    if (!container) {
      container = document.createElement('div');
      container.className = 'fdo-hidden-props';
      container.setAttribute('data-fdo-hidden-props', 'true');
      formEl.appendChild(container);
    }

    return container;
  }

  function upsertHiddenProperty(formEl, propertyName, value) {
    if (!formEl || !propertyName) return;

    var container = ensureHiddenContainer(formEl);
    if (!container) return;

    var key = safeKey(propertyName);
    var input = qs('[data-fdo-hidden-prop="' + key + '"]', container);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + propertyName + ']';
      input.setAttribute('data-fdo-hidden-prop', key);
      container.appendChild(input);
    }

    input.value = value || '';
  }

  function getPropertyName(field) {
    if (!field) return '';

    var name = field.getAttribute('name') || '';
    var match = name.match(/^properties\[(.+)\]$/);

    if (match && match[1]) return match[1];

    return field.getAttribute('data-fdo-prop') || '';
  }

  function copyModalPropertiesToProductForm(modalId) {
    var modal = document.getElementById(modalId);
    var productForm = getProductForm(modalId);

    if (!modal || !productForm) return;

    qsa('input, select, textarea', modal).forEach(function (field) {
      var propertyName = getPropertyName(field);
      if (!propertyName) return;

      if (field.type === 'radio' && !field.checked) return;

      upsertHiddenProperty(productForm, propertyName, getValue(field));
    });

    var context = getGiftContext(modalId);

    if (context && context.sourceOccasionId) {
      upsertHiddenProperty(productForm, 'Saved occasion ID', context.sourceOccasionId);
    }

    if (context && context.relationship) {
      upsertHiddenProperty(productForm, 'Relationship', context.relationship);
    }

    if (context && context.preferredGiftType) {
      upsertHiddenProperty(productForm, 'Preferred gift type', context.preferredGiftType);
    }

    if (context && context.style) {
      upsertHiddenProperty(productForm, 'Preferred style', context.style);
    }

    if (context && context.budget) {
      upsertHiddenProperty(productForm, 'Gift budget', context.budget);
    }
  }

  function moveModalToBody(modal) {
    if (!modal || modal.parentElement === document.body) return;
    document.body.appendChild(modal);
  }

  function openModal(modalId) {
    var modal = document.getElementById(modalId);
    if (!modal) return;

    var trigger = getTrigger(modalId);
    if (trigger) FDO.triggerMap[modalId] = trigger;

    moveModalToBody(modal);
    hydrateSavedOccasions(modalId);
    hydrateGiftContextFromFields(modalId);

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('role', 'dialog');

    document.documentElement.classList.add('fdo-modal-open');
    document.body.classList.add('fdo-modal-open');

    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    trapFocus(modal);
  }

  function closeModal(modalId) {
    var modal = document.getElementById(modalId);
    if (!modal) return;

    releaseFocus(modal);

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    if (!qs('.fdo-modal-overlay.is-open')) {
      document.documentElement.classList.remove('fdo-modal-open');
      document.body.classList.remove('fdo-modal-open');
    }

    var trigger = FDO.triggerMap[modalId] || getTrigger(modalId);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  }

  function closeAllModals() {
    qsa('.fdo-modal-overlay.is-open').forEach(function (modal) {
      closeModal(modal.id);
    });
  }

  function getCurrentStep(formEl) {
    var value = parseInt(formEl.getAttribute('data-current-step') || '1', 10);
    return isNaN(value) ? 1 : value;
  }

  function setStep(formEl, nextStep) {
    if (!formEl) return;

    var panels = qsa('.fdo-step-panel', formEl);
    var modal = formEl.closest('.fdo-modal-overlay');
    var pills = modal ? qsa('.fdo-step-pill', modal) : [];

    if (!panels.length) return;

    if (nextStep < 1) nextStep = 1;
    if (nextStep > panels.length) nextStep = panels.length;

    formEl.setAttribute('data-current-step', String(nextStep));

    panels.forEach(function (panel, index) {
      var isActive = index === nextStep - 1;
      panel.classList.toggle('active', isActive);
      panel.hidden = !isActive;
    });

    pills.forEach(function (pill, index) {
      pill.classList.toggle('active', index === nextStep - 1);
      pill.classList.toggle('done', index < nextStep - 1);
    });

    if (modal) {
      modal.setAttribute('data-fdo-active-step', String(nextStep));
    }
  }

  function nextStep(formId) {
    var formEl = document.getElementById(formId);
    if (!formEl) return;

    var modal = formEl.closest('.fdo-modal-overlay');
    if (modal) hydrateGiftContextFromFields(modal.id);

    setStep(formEl, getCurrentStep(formEl) + 1);
  }

  function prevStep(formId) {
    var formEl = document.getElementById(formId);
    if (!formEl) return;
    setStep(formEl, getCurrentStep(formEl) - 1);
  }

  /* -------------------------------
     Gift Context Engine
  -------------------------------- */

  function createEmptyGiftContext() {
    return {
      recipient: '',
      relationship: '',
      occasion: '',
      occasionDate: '',
      budget: '',
      style: '',
      preferredGiftType: '',
      giftMessage: '',
      reminderMethod: '',
      reminderTiming: '',
      deliveryDate: '',
      flowers: '',
      packaging: '',
      giftBox: '',
      giftCard: '',
      note: '',
      sourceOccasionId: null,
      sourceOccasionRaw: null
    };
  }

  function getGiftContext(modalId) {
    if (!FDO.contexts[modalId]) {
      FDO.contexts[modalId] = createEmptyGiftContext();
    }

    return FDO.contexts[modalId];
  }

  function updateGiftContext(modalId, patch) {
    var context = getGiftContext(modalId);

    Object.keys(patch || {}).forEach(function (key) {
      context[key] = patch[key];
    });

    renderGiftContext(modalId);
    return context;
  }

  function buildGiftContextFromOccasion(occasion) {
    occasion = occasion || {};

    return {
      recipient:
        occasion.name ||
        occasion.recipientName ||
        occasion.recipient ||
        occasion.personName ||
        '',

      relationship:
        occasion.relationship ||
        occasion.relation ||
        '',

      occasion:
        occasion.type ||
        occasion.occasion ||
        occasion.occasionType ||
        '',

      occasionDate:
        normalizeDate(
          occasion.date ||
          occasion.occasionDate ||
          occasion.nextDate ||
          ''
        ),

      budget:
        occasion.budget ||
        occasion.giftBudget ||
        occasion.preferredBudget ||
        '',

      style:
        occasion.style ||
        occasion.preferredStyle ||
        occasion.jewelryStyle ||
        '',

      preferredGiftType:
        occasion.preferredGiftType ||
        occasion.giftType ||
        occasion.giftWish ||
        occasion.giftPreference ||
        '',

      giftMessage:
        occasion.message ||
        occasion.giftMessage ||
        occasion.note ||
        occasion.notes ||
        '',

      note:
        occasion.note ||
        occasion.notes ||
        '',

      reminderMethod:
        occasion.reminderMethod ||
        occasion.method ||
        '',

      reminderTiming:
        occasion.reminderTiming ||
        occasion.timing ||
        '',

      sourceOccasionId:
        occasion.id ||
        occasion.occasionId ||
        null,

      sourceOccasionRaw: occasion
    };
  }

  function hydrateGiftContextFromFields(modalId) {
    var modal = document.getElementById(modalId);
    if (!modal) return;

    updateGiftContext(modalId, {
      recipient: getValue(findField(modal, 'recipient')),
      occasion: getValue(findField(modal, 'occasion')),
      occasionDate: getValue(findField(modal, 'occasionDate')),
      giftMessage: getValue(findField(modal, 'message')),
      budget: getValue(findField(modal, 'budget')),
      style: getValue(findField(modal, 'style')),
      preferredGiftType: getValue(findField(modal, 'giftType')),
      reminderMethod: getValue(findField(modal, 'reminder')),
      deliveryDate: getValue(findField(modal, 'deliveryDate')),
      giftBox: getValue(findField(modal, 'box')),
      giftCard: getValue(findField(modal, 'card')),
      flowers: getValue(findField(modal, 'flowers')),
      packaging: getValue(findField(modal, 'packaging'))
    });
  }

  function renderGiftContext(modalId) {
    var modal = document.getElementById(modalId);
    var context = getGiftContext(modalId);

    if (!modal || !context) return;

    setFieldValue(findField(modal, 'recipient'), context.recipient);
    setFieldValue(findField(modal, 'occasion'), context.occasion);
    setFieldValue(findField(modal, 'occasionDate'), context.occasionDate);
    setFieldValue(findField(modal, 'message'), context.giftMessage);
    setFieldValue(findField(modal, 'budget'), context.budget);
    setFieldValue(findField(modal, 'style'), context.style);
    setFieldValue(findField(modal, 'giftType'), context.preferredGiftType);
    setFieldValue(findField(modal, 'reminder'), context.reminderMethod);
    setFieldValue(findField(modal, 'deliveryDate'), context.deliveryDate);
    setFieldValue(findField(modal, 'box'), context.giftBox);
    setFieldValue(findField(modal, 'card'), context.giftCard);
    setFieldValue(findField(modal, 'flowers'), context.flowers);
    setFieldValue(findField(modal, 'packaging'), context.packaging);

    modal.classList.add('fdo-context-rendered');

    window.setTimeout(function () {
      modal.classList.remove('fdo-context-rendered');
    }, 700);
  }

  function findField(modal, key) {
    if (!modal || !key) return null;

    var selectors = {
      recipient: [
        '[data-fdo-summary-source="recipient"]',
        '[data-fdo-field="recipient"]',
        'input[name="properties[Recipient name]"]',
        'input[name="properties[Recipient]"]'
      ],

      occasion: [
        '[data-fdo-summary-source="occasion"]',
        '[data-fdo-field="occasion"]',
        'select[name="properties[Occasion]"]',
        'input[name="properties[Occasion]"]'
      ],

      occasionDate: [
        '[data-fdo-field="occasion-date"]',
        'input[name="properties[Occasion date]"]',
        'input[name="properties[Occasion Date]"]'
      ],

      deliveryDate: [
        '[data-fdo-field="delivery-date"]',
        'input[name="properties[Preferred delivery date]"]',
        'input[name="properties[Delivery date]"]'
      ],

      message: [
        '[data-fdo-summary-source="message"]',
        '[data-fdo-field="message"]',
        'textarea[name="properties[Gift message]"]',
        'textarea[name="properties[Gift Message]"]',
        'input[name="properties[Gift message]"]'
      ],

      budget: [
        '[data-fdo-field="budget"]',
        'select[name="properties[Budget]"]',
        'input[name="properties[Budget]"]',
        'select[name="properties[Gift budget]"]',
        'input[name="properties[Gift budget]"]'
      ],

      style: [
        '[data-fdo-field="style"]',
        'select[name="properties[Style]"]',
        'input[name="properties[Style]"]',
        'select[name="properties[Preferred style]"]',
        'input[name="properties[Preferred style]"]'
      ],

      giftType: [
        '[data-fdo-field="gift-type"]',
        'select[name="properties[Gift type]"]',
        'input[name="properties[Gift type]"]',
        'select[name="properties[Preferred gift type]"]',
        'input[name="properties[Preferred gift type]"]'
      ],

      reminder: [
        '[data-fdo-summary-source="reminder"]',
        '[data-fdo-field="reminder"]',
        'select[name="properties[Reminder]"]',
        'input[name="properties[Reminder]"]',
        'select[name="properties[Reminder method]"]'
      ],

      box: [
        '[data-fdo-summary-source="box"]',
        '[data-fdo-field="box"]',
        'input[name="properties[Gift box]"]',
        'select[name="properties[Gift box]"]'
      ],

      card: [
        '[data-fdo-summary-source="card"]',
        '[data-fdo-field="card"]',
        'input[name="properties[Gift card]"]',
        'select[name="properties[Gift card]"]'
      ],

      flowers: [
        '[data-fdo-field="flowers"]',
        'input[name="properties[Flowers]"]',
        'select[name="properties[Flowers]"]'
      ],

      packaging: [
        '[data-fdo-field="packaging"]',
        'input[name="properties[Packaging]"]',
        'select[name="properties[Packaging]"]'
      ]
    };

    var list = selectors[key] || [];

    for (var i = 0; i < list.length; i++) {
      var found = qs(list[i], modal);
      if (found) return found;
    }

    return null;
  }

  /* -------------------------------
     Summary
  -------------------------------- */

  function findSummary(modalId) {
    return qs('[data-fdo-summary="' + modalId + '"]');
  }

  function setSummaryLine(summary, key, label, value) {
    if (!summary) return;

    var line = qs('[data-fdo-summary-line="' + key + '"]', summary);
    if (!line) return;

    if (value) {
      line.textContent = label + ': ' + value;
      line.hidden = false;
    } else {
      line.textContent = '';
      line.hidden = true;
    }
  }

  function updateSummary(modalFormEl, modalId) {
    var summary = findSummary(modalId);
    if (!summary || !modalFormEl) return;

    hydrateGiftContextFromFields(modalId);

    var context = getGiftContext(modalId);

    setSummaryLine(summary, 'recipient', 'Recipient', context.recipient);
    setSummaryLine(summary, 'occasion', 'Occasion', context.occasion);
    setSummaryLine(summary, 'date', 'Occasion date', formatDate(context.occasionDate));
    setSummaryLine(summary, 'budget', 'Budget', context.budget);
    setSummaryLine(summary, 'style', 'Style', context.style);
    setSummaryLine(summary, 'giftType', 'Gift type', context.preferredGiftType);
    setSummaryLine(summary, 'message', 'Message', context.giftMessage ? 'Added' : '');
    setSummaryLine(summary, 'box', 'Gift box', context.giftBox);
    setSummaryLine(summary, 'card', 'Gift card', context.giftCard);
    setSummaryLine(summary, 'flowers', 'Flowers', context.flowers);
    setSummaryLine(summary, 'reminder', 'Reminder', context.reminderMethod);

    summary.hidden = false;
    summary.classList.add('is-visible');
  }

  function saveCustomizer(payload) {
    if (!payload) return;

    var parts = payload.split('|');
    var modalFormId = parts[0];
    var modalId = parts[1];

    var modalFormEl = document.getElementById(modalFormId);
    if (!modalFormEl || !modalId) return;

    hydrateGiftContextFromFields(modalId);
    copyModalPropertiesToProductForm(modalId);
    updateSummary(modalFormEl, modalId);
    closeModal(modalId);
  }

  /* -------------------------------
     Saved occasions
  -------------------------------- */

  function getCustomerData() {
    var scripts = qsa('script[id^="fdo-customer-data-"]');

    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent || '{}');
        if (data && data.id) return data;
      } catch (e) {}
    }

    return null;
  }

  function getSavedOccasions() {
    var customer = getCustomerData();
    if (!customer || !customer.id) return [];

    try {
      var raw = localStorage.getItem('fdo_' + customer.id);
      if (!raw) return [];

      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.occasions)) return [];

      return parsed.occasions;
    } catch (e) {
      return [];
    }
  }

  function normalizeDate(dateStr) {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

    var d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';

    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');

    return y + '-' + m + '-' + day;
  }

  function formatDate(dateStr) {
    var normalized = normalizeDate(dateStr);
    if (!normalized) return '';

    var parts = normalized.split('-');
    var d = new Date(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10)
    );

    if (isNaN(d.getTime())) return normalized;

    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  function getDaysUntil(dateStr) {
    var normalized = normalizeDate(dateStr);
    if (!normalized) return null;

    var parts = normalized.split('-');
    var now = new Date();
    var target = new Date(
      now.getFullYear(),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10)
    );

    var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (target < today) {
      target.setFullYear(target.getFullYear() + 1);
    }

    var diff = target.getTime() - today.getTime();
    return Math.ceil(diff / 86400000);
  }

  function getCountdownLabel(dateStr) {
    var days = getDaysUntil(dateStr);

    if (days === null) return '';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days <= 14) return 'In ' + days + ' days';
    if (days <= 45) return 'Soon';

    return 'In ' + days + ' days';
  }

  function getOccasionIcon(type) {
    var normalized = String(type || '').toLowerCase();

    if (normalized.indexOf('birthday') !== -1) return '🎂';
    if (normalized.indexOf('anniversary') !== -1) return '❤️';
    if (normalized.indexOf('wedding') !== -1) return '💍';
    if (normalized.indexOf('engagement') !== -1) return '💍';
    if (normalized.indexOf('christmas') !== -1) return '🎄';
    if (normalized.indexOf('mother') !== -1) return '🌷';
    if (normalized.indexOf('father') !== -1) return '⌚';
    if (normalized.indexOf('valentine') !== -1) return '♡';
    if (normalized.indexOf('graduation') !== -1) return '🎓';

    return '✦';
  }

  function applyOccasionToModal(modal, occasion) {
    if (!modal || !occasion) return;

    var modalId = modal.id;
    var contextPatch = buildGiftContextFromOccasion(occasion);

    updateGiftContext(modalId, contextPatch);

    modal.classList.add('fdo-autofilled');

    window.setTimeout(function () {
      modal.classList.remove('fdo-autofilled');
    }, 900);
  }

  function renderSavedOccasions(modalId) {
    var modal = document.getElementById(modalId);
    if (!modal) return;

    var savedBox = qs('.fdo-saved-days', modal);
    if (!savedBox) return;

    var occasions = getSavedOccasions();

    if (!occasions.length) {
      savedBox.innerHTML =
        '<div class="fdo-saved-days-header">' +
          '<strong>The moments you save today become memories they’ll wear forever.</strong>' +
          '<p>Your saved occasions will appear here once added to your profile.</p>' +
        '</div>';
      return;
    }

    var html =
      '<div class="fdo-saved-days-header">' +
        '<strong>Use a saved important day</strong>' +
        '<p>Select one of your saved occasions and we’ll prepare this gift around it.</p>' +
      '</div>' +
      '<div class="fdo-saved-occasion-list">';

    occasions.slice(0, 6).forEach(function (occ, index) {
      var name = occ.name || occ.recipientName || 'Someone special';
      var type = occ.type || occ.occasion || 'Occasion';
      var date = occ.date || occ.occasionDate || '';
      var countdown = getCountdownLabel(date);
      var icon = getOccasionIcon(type);

      html +=
        '<button type="button" class="fdo-saved-occasion-choice" data-fdo-occasion-index="' + index + '">' +
          '<span class="fdo-saved-occasion-icon" aria-hidden="true">' + escapeHtml(icon) + '</span>' +
          '<span class="fdo-saved-occasion-content">' +
            '<span class="fdo-saved-occasion-name">' + escapeHtml(name) + '</span>' +
            '<span class="fdo-saved-occasion-meta">' +
              escapeHtml(type) +
              (date ? ' · ' + escapeHtml(formatDate(date)) : '') +
            '</span>' +
          '</span>' +
          (countdown ? '<span class="fdo-saved-occasion-countdown">' + escapeHtml(countdown) + '</span>' : '') +
        '</button>';
    });

    html += '</div>';

    savedBox.innerHTML = html;

    qsa('[data-fdo-occasion-index]', savedBox).forEach(function (button) {
      button.addEventListener('click', function () {
        var index = parseInt(button.getAttribute('data-fdo-occasion-index'), 10);
        var selected = occasions[index];

        applyOccasionToModal(modal, selected);

        qsa('[data-fdo-occasion-index]', savedBox).forEach(function (btn) {
          btn.classList.remove('selected');
          btn.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('selected');
        button.setAttribute('aria-pressed', 'true');
      });
    });
  }

  function hydrateSavedOccasions(modalId) {
    renderSavedOccasions(modalId);
  }

  /* -------------------------------
     Live context sync
  -------------------------------- */

  function syncContextFromChangedField(field) {
    if (!field) return;

    var modal = field.closest('.fdo-modal-overlay');
    if (!modal || !modal.id) return;

    var context = getGiftContext(modal.id);
    var value = getValue(field);

    if (field.matches('[data-fdo-summary-source="recipient"], [data-fdo-field="recipient"], input[name="properties[Recipient name]"], input[name="properties[Recipient]"]')) {
      context.recipient = value;
    }

    if (field.matches('[data-fdo-summary-source="occasion"], [data-fdo-field="occasion"], select[name="properties[Occasion]"], input[name="properties[Occasion]"]')) {
      context.occasion = value;
    }

    if (field.matches('[data-fdo-field="occasion-date"], input[name="properties[Occasion date]"], input[name="properties[Occasion Date]"]')) {
      context.occasionDate = value;
    }

    if (field.matches('[data-fdo-summary-source="message"], [data-fdo-field="message"], textarea[name="properties[Gift message]"], textarea[name="properties[Gift Message]"], input[name="properties[Gift message]"]')) {
      context.giftMessage = value;
    }
  }

  /* -------------------------------
     Events
  -------------------------------- */

  document.addEventListener('click', function (event) {
    var openBtn = event.target.closest('[data-fdo-open]');
    if (openBtn) {
      event.preventDefault();
      openModal(openBtn.getAttribute('data-fdo-open'));
      return;
    }

    var closeBtn = event.target.closest('[data-fdo-close]');
    if (closeBtn) {
      event.preventDefault();
      closeModal(closeBtn.getAttribute('data-fdo-close'));
      return;
    }

    var nextBtn = event.target.closest('[data-fdo-next]');
    if (nextBtn) {
      event.preventDefault();
      nextStep(nextBtn.getAttribute('data-fdo-next'));
      return;
    }

    var prevBtn = event.target.closest('[data-fdo-prev]');
    if (prevBtn) {
      event.preventDefault();
      prevStep(prevBtn.getAttribute('data-fdo-prev'));
      return;
    }

    var stepBtn = event.target.closest('[data-fdo-go-step]');
    if (stepBtn) {
      event.preventDefault();

      var formId = stepBtn.getAttribute('data-fdo-go-step-form');
      var targetStep = parseInt(stepBtn.getAttribute('data-fdo-go-step'), 10);
      var formEl = document.getElementById(formId);

      if (formEl && !isNaN(targetStep)) setStep(formEl, targetStep);
      return;
    }

    var saveBtn = event.target.closest('[data-fdo-save]');
    if (saveBtn) {
      event.preventDefault();
      saveCustomizer(saveBtn.getAttribute('data-fdo-save'));
      return;
    }

    if (event.target.classList && event.target.classList.contains('fdo-modal-overlay')) {
      event.preventDefault();
      closeModal(event.target.id);
    }
  });

  document.addEventListener('input', function (event) {
    syncContextFromChangedField(event.target);
  });

  document.addEventListener('change', function (event) {
    syncContextFromChangedField(event.target);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeAllModals();
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    qsa('[data-current-step]').forEach(function (formEl) {
      setStep(formEl, getCurrentStep(formEl));
    });
  });

  window.FlorDeOroCustomizer = {
    open: openModal,
    close: closeModal,
    getContext: getGiftContext,
    updateContext: updateGiftContext,
    hydrateSavedOccasions: hydrateSavedOccasions
  };
})();